import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Linkedin } from "lucide-react";

const roles = [
  "Lead SDET",
  "Automation Tester",
  "Performance Tester",
  "API Tester",
  "Quality Engineer",
];

const stats = [
  { value: "7+", label: "Years Experience", targetId: "experience" },
  { value: "4", label: "Domains" },
  { value: "7+", label: "Innovations", targetId: "innovations" },
];

const About = ({ setActiveTab }) => {
  const aboutRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [currentRole, setCurrentRole] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Rotating role animation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRole((prev) => (prev + 1) % roles.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);

    const currentRef = aboutRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowScrollIndicator(entry.isIntersecting);
      },
      { threshold: isMobile ? 0.5 : 1.0 }
    );

    if (currentRef) observer.observe(currentRef);

    return () => {
      window.removeEventListener("resize", checkMobile);
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [isMobile]);

  const handleScrollClick = () => {
    const sectionsDiv = document.getElementById("sections-container");
    if (sectionsDiv) {
      sectionsDiv.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section
      id="about"
      ref={aboutRef}
      className="relative min-h-screen flex items-center px-4 sm:px-6 md:px-20 py-20 bg-white dark:bg-gray-900 transition-colors duration-300 overflow-hidden"
    >
      {/* Animated gradient background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 -left-10 w-72 h-72 bg-blue-400/20 dark:bg-blue-500/10 rounded-full blur-3xl"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-10 w-96 h-96 bg-purple-400/15 dark:bg-purple-500/10 rounded-full blur-3xl"
          animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/3 w-64 h-64 bg-cyan-400/10 dark:bg-cyan-500/5 rounded-full blur-3xl"
          animate={{ x: [0, 40, 0], y: [0, -40, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-10 text-center md:text-left relative z-10">
        {/* Profile Image with glow ring */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="relative group"
        >
          <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur-md opacity-40 group-hover:opacity-60 transition-opacity duration-300" />
          <div className="relative w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-white dark:border-gray-800 shadow-xl transform transition duration-300 hover:scale-105">

            {/* Skeleton shimmer — visible until image loads */}
            <AnimatePresence>
              {!imageLoaded && (
                <motion.div
                  key="skeleton"
                  className="absolute inset-0 rounded-full skeleton-shimmer"
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                />
              )}
            </AnimatePresence>

            {/* Profile image — fades in on load */}
            <img
              src={`${process.env.PUBLIC_URL}/gulshan-kumar.jpg`}
              alt="Gulshan Kumar Profile"
              onLoad={() => setImageLoaded(true)}
              className={`w-full h-full object-cover transition-opacity duration-700 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </div>
        </motion.div>

        {/* About Text */}
        <div className="max-w-2xl px-2">
          {/* Animated role tagline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-4"
          >
            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
              I am a
            </span>
            <div className="h-10 sm:h-12 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.h2
                  key={currentRole}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                  className="text-2xl sm:text-3xl font-bold font-display bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent"
                >
                  {roles[currentRole]}
                </motion.h2>
              </AnimatePresence>
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-base sm:text-lg text-gray-600 dark:text-gray-300 leading-relaxed transition-colors duration-300"
          >
            Hello! I'm{" "}
            <strong className="text-blue-600 dark:text-blue-400">Gulshan Kumar</strong>, a{" "}
            <span className="text-blue-600 dark:text-blue-400 font-bold">Lead SDET</span> at{" "}
            <strong className="text-blue-600 dark:text-blue-400">S&P Global</strong> with 7+
            years of experience across test automation, API, and performance testing. I specialize in building robust automation frameworks using Java, Selenium, Playwright, Karate, and TestNG, with a core focus on AI-driven test automation and self-healing solutions to improve software quality.
          </motion.p>

          {/* Stats counters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="flex gap-8 mt-6 justify-center md:justify-start"
          >
            {stats.map((stat, i) => {
              const isClickable = !!stat.targetId;
              
              if (isClickable) {
                const handleClick = (e) => {
                  e.preventDefault();
                  if (setActiveTab) {
                    setActiveTab(stat.targetId);
                  }
                };

                return (
                  <a 
                    href={`#${stat.targetId}`}
                    onClick={handleClick}
                    key={i} 
                    className="text-center md:text-left cursor-pointer group block"
                  >
                    <motion.span
                      className="inline-block text-3xl sm:text-4xl font-extrabold font-display bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent transition-transform duration-300 group-hover:scale-110"
                      initial={{ opacity: 0, scale: 0.5 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
                    >
                      {stat.value}
                    </motion.span>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 underline decoration-transparent group-hover:decoration-blue-600 dark:group-hover:decoration-blue-400 underline-offset-4">
                      {stat.label}
                    </p>
                  </a>
                );
              }

              return (
                <div key={i} className="text-center md:text-left">
                  <motion.span
                    className="inline-block text-3xl sm:text-4xl font-extrabold font-display bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent"
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
                  >
                    {stat.value}
                  </motion.span>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">
                    {stat.label}
                  </p>
                </div>
              );
            })}
          </motion.div>

          {/* Email and LinkedIn */}
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 text-blue-500 dark:text-blue-400 text-base sm:text-lg mt-6 justify-center md:justify-start transition-colors duration-300">
            <a
              href="mailto:gulshan.sdet@gmail.com"
              className="flex items-center gap-2 hover:underline hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer transition-colors duration-200"
            >
              <Mail className="text-xl sm:text-2xl" />
              <span>gulshan.sdet@gmail.com</span>
            </a>
            <a
              href="https://www.linkedin.com/in/gulshan98/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:underline hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer transition-colors duration-200"
            >
              <Linkedin className="text-xl sm:text-2xl" />
              <span>LinkedIn Profile</span>
            </a>
          </div>

          {/* Mobile scroll down indicator */}
          {showScrollIndicator && isMobile && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: [10, 0, 10] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="block sm:hidden mt-6 text-blue-600 dark:text-blue-400 text-sm flex flex-col items-center cursor-pointer transition-colors duration-300"
              onClick={handleScrollClick}
            >
              <span>Scroll Down</span>
              <svg className="w-6 h-6 mt-1 animate-bounce" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </motion.div>
          )}
        </div>
      </div>

      {/* Desktop scroll down indicator — positioned relative to the section */}
      {showScrollIndicator && !isMobile && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: [10, 0, 10] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 mb-6 text-blue-600 dark:text-blue-400 text-sm cursor-pointer flex flex-col items-center space-y-2 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-300 z-10"
          onClick={handleScrollClick}
        >
          <span>Scroll Down</span>
          <svg className="w-6 h-6 mt-1 animate-bounce" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      )}
    </section>
  );
};

export default About;